import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import {
  FiServer, FiCpu, FiHardDrive, FiActivity, FiSettings,
  FiLogOut, FiShoppingCart, FiShoppingBag, FiGlobe, FiMapPin,
  FiMail, FiKey, FiAlertCircle, FiCheckCircle
} from 'react-icons/fi';

const API_URL = process.env.REACT_APP_API_URL;

interface ServiceItem {
  id: number;
  service_type_id: number;
  service_type_name: string;
  hsn_code: string | null;
  name: string;
  price: number | string;
  billing_cycle: string;
  status: string;
  start_date: string | null;
  expiry_date: string | null;
  server_name: string | null;
  ip_address: string | null;
  cpu: string | null;
  ram: string | null;
  hd: string | null;
  backup: string | null;
  panel: string | null;
  username: string | null;
  password: string | null;
  remarks: string | null;
}

interface ServiceType {
  id: number;
  company_id: number | null;
  name: string;
  hsn_code: string | null;
  price: number | string;
  has_hosting_details: number;
}

interface Company {
  id: number;
  company_name: string;
}

function CustomerPortal() {
  const [activeTab, setActiveTab] = useState<'my-services' | 'buy-services'>('my-services');
  const [purchasedServices, setPurchasedServices] = useState<ServiceItem[]>([]);
  const [availableServices, setAvailableServices] = useState<ServiceType[]>([]);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [customerInfo, setCustomerInfo] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [kycFileUploading, setKycFileUploading] = useState(false);
  const [kycPan, setKycPan] = useState('');
  const [kycDocUrl, setKycDocUrl] = useState('');

  // Purchase Modal
  const [isPurchaseModalOpen, setIsPurchaseModalOpen] = useState(false);
  const [selectedService, setSelectedService] = useState<ServiceType | null>(null);

  // Purchase Form fields
  const [purchaseForm, setPurchaseForm] = useState({
    company_name: '',
    email: '',
    password: '',
    address: '',
    city: '',
    state: '',
    country: 'India',
    gst_number: '',
    company_id: '', // provider company
    billing_cycle: 'Monthly'
  });

  const navigate = useNavigate();
  const token = localStorage.getItem('token');
  const role = localStorage.getItem('role');

  // Verify auth
  useEffect(() => {
    if (!token || role !== 'customer') {
      navigate('/customer/login');
    }
  }, [token, role, navigate]);

  const fetchCustomerData = async () => {
    setLoading(true);
    try {
      const [myServicesRes, availableRes, companiesRes, meRes] = await Promise.all([
        axios.get(`${API_URL}/auth/customer/services`),
        axios.get(`${API_URL}/auth/customer/available-services`),
        axios.get(`${API_URL}/auth/companies`), // fetch active companies in system
        axios.get(`${API_URL}/auth/customer/me`),
      ]);
      setPurchasedServices(Array.isArray(myServicesRes.data) ? myServicesRes.data : []);
      setAvailableServices(Array.isArray(availableRes.data) ? availableRes.data : []);
      setCompanies(Array.isArray(companiesRes.data) ? companiesRes.data : []);
      setCustomerInfo(meRes.data);

      // Prefill form if customer info is retrieved
      if (meRes.data) {
        setPurchaseForm(prev => ({
          ...prev,
          company_name: meRes.data.company_name || '',
          email: meRes.data.email || '',
          address: meRes.data.address || '',
          city: meRes.data.city || '',
          state: meRes.data.state || '',
          gst_number: meRes.data.gst_number || ''
        }));
        setKycPan(meRes.data.pancard || '');
        setKycDocUrl(meRes.data.kyc_document_url || '');
      }
    } catch (err) {
      setError('Error loading customer portal data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token && role === 'customer') {
      fetchCustomerData();
    }
  }, [token, role]);

  const handleClientKycUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setKycFileUploading(true);
      const formData = new FormData();
      formData.append('file', file);
      try {
        const res = await axios.post(`${API_URL}/auth/upload-attachment`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        if (res.data && res.data.file_url) {
          setKycDocUrl(res.data.file_url);
          setSuccess("KYC Document uploaded. Click 'Save KYC Profile' to save changes.");
        }
      } catch (err) {
        setError("Failed to upload KYC document file.");
      } finally {
        setKycFileUploading(false);
      }
    }
  };

  const handleSaveKycProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    try {
      await axios.put(`${API_URL}/auth/customer/me/kyc`, {
        pancard: kycPan,
        kyc_document_url: kycDocUrl
      });
      setSuccess("KYC Profile updated successfully!");
      fetchCustomerData();
    } catch (err: any) {
      setError(err.response?.data?.error || "Failed to update KYC profile");
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('role');
    localStorage.removeItem('customerId');
    localStorage.removeItem('customerName');
    navigate('/customer/login');
  };

  const handleStatusChange = async (serviceId: number, newStatus: string) => {
    setError('');
    setSuccess('');
    try {
      await axios.put(`${API_URL}/auth/customer/services/${serviceId}/status`, { status: newStatus });
      setSuccess('Service status update requested successfully!');
      fetchCustomerData();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to update service status');
    }
  };

  const handleOpenPurchase = (service: ServiceType) => {
    setSelectedService(service);
    setPurchaseForm(prev => ({
      ...prev,
      company_id: service.company_id ? String(service.company_id) : (companies.length > 0 ? String(companies[0].id) : '')
    }));
    setIsPurchaseModalOpen(true);
  };

  const handleConfirmPurchase = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    if (!selectedService) return;

    const payload = {
      ...purchaseForm,
      service_type_id: selectedService.id,
      company_id: Number(purchaseForm.company_id)
    };

    try {
      const response = await axios.post(`${API_URL}/auth/services/purchase`, payload);
      setSuccess(`Service "${selectedService.name}" purchased successfully! Invoice generated: ${response.data.invoiceNo}`);
      setIsPurchaseModalOpen(false);
      fetchCustomerData();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to complete service purchase');
    }
  };

  return (
    <div className="min-h-screen bg-slate-50/50 flex flex-col font-sans">
      {/* Navbar */}
      <nav className="bg-[#4c1d95] text-white py-4 px-6 shadow-lg flex items-center justify-between">
        <div className="flex items-center gap-3">
          <FiServer size={24} className="text-violet-200 animate-pulse" />
          <span className="text-xl font-black tracking-tight">Customer Services Panel</span>
        </div>
        <div className="flex items-center gap-4">
          <span className="text-sm font-semibold text-violet-100 hidden sm:inline">
            Welcome, <strong className="text-white">{customerInfo?.company_name || 'Customer'}</strong>
          </span>
          <button
            onClick={handleLogout}
            className="flex items-center gap-2 px-3 py-1.5 bg-violet-800 hover:bg-violet-900 rounded-xl text-xs font-bold transition shadow-sm"
          >
            <FiLogOut /> Log Out
          </button>
        </div>
      </nav>

      {/* Main View Container */}
      <div className="flex-1 max-w-6xl w-full mx-auto p-4 md:p-8 space-y-6">
        {error && (
          <div className="p-4 bg-red-50 border border-red-200 text-red-600 rounded-xl text-sm font-medium flex items-center gap-2 shadow-sm animate-shake">
            <FiAlertCircle size={18} />
            <span>{error}</span>
          </div>
        )}
        {success && (
          <div className="p-4 bg-green-50 border border-green-200 text-green-700 rounded-xl text-sm font-semibold flex items-center gap-2 shadow-sm animate-pulse">
            <FiCheckCircle size={18} />
            <span>{success}</span>
          </div>
        )}

        {/* KYC Verification Widget */}
        <div className="bg-white border border-slate-100 rounded-3xl p-6 shadow-md text-left">
          <div className="flex justify-between items-center border-b border-slate-100 pb-3 mb-4">
            <div>
              <h3 className="text-md font-bold text-violet-950 flex items-center gap-2">
                <FiKey className="text-violet-600" /> KYC Verification Profile
              </h3>
              <p className="text-xs text-gray-500 mt-0.5">Please provide your PAN card and upload a valid KYC document (PDF/Image).</p>
            </div>
            <span className={`px-2.5 py-1 rounded-full text-xs font-semibold uppercase ${
              kycDocUrl ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-800'
            }`}>
              {kycDocUrl ? 'Uploaded / Verified' : 'KYC Pending'}
            </span>
          </div>

          <form onSubmit={handleSaveKycProfile} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-bold text-gray-600 uppercase mb-1">PAN Card Number</label>
                <input
                  type="text"
                  value={kycPan}
                  onChange={e => setKycPan(e.target.value.toUpperCase())}
                  maxLength={10}
                  className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500 bg-white uppercase font-semibold"
                  placeholder="ABCDE1234F"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-gray-600 uppercase mb-1">Upload KYC Document (PDF or Image)</label>
                <div className="flex gap-2 items-center">
                  <input
                    type="file"
                    accept=".pdf,image/*"
                    onChange={handleClientKycUpload}
                    disabled={kycFileUploading}
                    className="text-xs text-slate-500 file:mr-4 file:py-2 file:px-4 file:rounded-xl file:border-0 file:text-xs file:font-semibold file:bg-violet-50 file:text-violet-700 hover:file:bg-violet-100 cursor-pointer"
                  />
                  {kycFileUploading && <span className="text-[11px] text-violet-600 animate-pulse">Uploading...</span>}
                </div>
              </div>
            </div>

            <div className="flex justify-between items-center pt-2">
              <div>
                {kycDocUrl && (
                  <a
                    href={kycDocUrl.startsWith('http') ? kycDocUrl : `${API_URL}${kycDocUrl.startsWith('/') ? '' : '/'}${kycDocUrl}`}
                    target="_blank"
                    rel="noreferrer"
                    className="text-xs text-violet-600 hover:text-violet-800 font-bold underline flex items-center gap-1"
                  >
                    📎 View My Uploaded KYC Document
                  </a>
                )}
              </div>

              <button
                type="submit"
                className="px-5 py-2 bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-700 hover:to-indigo-700 text-white text-xs font-bold rounded-xl shadow transition"
              >
                Save KYC Profile
              </button>
            </div>
          </form>
        </div>

        {/* Tab Selection */}
        <div className="flex border-b border-violet-100 pb-px">
          <button
            onClick={() => setActiveTab('my-services')}
            className={`flex items-center gap-2 px-5 py-3 border-b-2 font-bold text-sm transition ${
              activeTab === 'my-services'
                ? 'border-violet-600 text-violet-700'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <FiShoppingBag /> My Subscriptions ({purchasedServices.length})
          </button>
          <button
            onClick={() => setActiveTab('buy-services')}
            className={`flex items-center gap-2 px-5 py-3 border-b-2 font-bold text-sm transition ${
              activeTab === 'buy-services'
                ? 'border-violet-600 text-violet-700'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <FiShoppingCart /> Purchase Services
          </button>
        </div>

        {/* Dynamic tabs */}
        {loading ? (
          <div className="flex justify-center items-center py-20">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-violet-700"></div>
          </div>
        ) : activeTab === 'my-services' ? (
          purchasedServices.length === 0 ? (
            <div className="bg-white border border-slate-100 rounded-3xl p-12 text-center max-w-xl mx-auto shadow-sm">
              <FiShoppingBag className="mx-auto text-5xl text-violet-200 mb-4" />
              <h3 className="text-lg font-bold text-violet-950">No Active Services</h3>
              <p className="text-gray-500 text-sm mt-2">You don't have any registered services yet. Head over to "Purchase Services" to request a subscription.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {purchasedServices.map(item => (
                <div key={item.id} className="bg-white border border-slate-100 rounded-2xl p-6 shadow-md hover:shadow-xl transition-all duration-150 flex flex-col justify-between">
                  <div>
                    <div className="flex items-start justify-between">
                      <div>
                        <span className="px-2.5 py-0.5 bg-violet-100 text-violet-800 text-[10px] uppercase font-bold rounded-full">
                          {item.service_type_name}
                        </span>
                        <h3 className="text-lg font-bold text-slate-800 mt-2">{item.name}</h3>
                        {item.hsn_code && <span className="text-[10px] text-gray-400 font-medium">HSN/SAC: {item.hsn_code}</span>}
                      </div>
                      <span className={`px-2.5 py-1 rounded-full text-xs font-semibold uppercase ${
                        item.status === 'Active' ? 'bg-green-100 text-green-800' :
                        item.status === 'Suspended' ? 'bg-amber-100 text-amber-800' : 'bg-red-100 text-red-800'
                      }`}>
                        {item.status}
                      </span>
                    </div>

                    <div className="mt-4 grid grid-cols-2 gap-4 bg-slate-50/50 rounded-xl p-3 border border-slate-100">
                      <div>
                        <span className="text-[10px] uppercase text-gray-400 font-bold block">Rate / Price</span>
                        <span className="text-sm font-bold text-slate-700">₹{Number(item.price).toLocaleString('en-IN')} / {item.billing_cycle}</span>
                      </div>
                      <div>
                        <span className="text-[10px] uppercase text-gray-400 font-bold block">Start Date</span>
                        <span className="text-sm font-bold text-slate-700">{item.start_date ? new Date(item.start_date).toLocaleDateString('en-IN') : 'N/A'}</span>
                      </div>
                    </div>

                    {item.server_name && (
                      <div className="mt-5 border-t border-dashed border-slate-100 pt-4 space-y-2.5">
                        <h4 className="text-xs font-extrabold text-violet-900 uppercase tracking-wider mb-2 flex items-center gap-1">
                          <FiServer /> Server Specs
                        </h4>
                        <div className="grid grid-cols-2 gap-2 text-xs text-slate-600">
                          <span className="truncate">Host: <strong>{item.server_name}</strong></span>
                          <span>IP: <strong>{item.ip_address || 'N/A'}</strong></span>
                          <span>CPU: <strong>{item.cpu || 'N/A'}</strong></span>
                          <span>RAM: <strong>{item.ram || 'N/A'}</strong></span>
                          <span>Storage: <strong>{item.hd || 'N/A'}</strong></span>
                          <span>Control Panel: <strong>{item.panel || 'N/A'}</strong></span>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="mt-6 border-t border-slate-100 pt-4 flex items-center justify-between">
                    <span className="text-xs text-slate-400">Request status changes:</span>
                    <select
                      value={item.status}
                      onChange={e => handleStatusChange(item.id, e.target.value)}
                      className="px-3 py-1.5 border border-slate-200 rounded-lg text-xs font-bold bg-white text-slate-700 focus:outline-none"
                    >
                      <option value="Active">Active</option>
                      <option value="Suspended">Request Suspension</option>
                      <option value="Terminated">Request Termination</option>
                    </select>
                  </div>
                </div>
              ))}
            </div>
          )
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {availableServices.map(service => (
              <div key={service.id} className="bg-white border border-slate-100 rounded-2xl p-6 shadow-md hover:shadow-xl transition-all duration-150 flex flex-col justify-between">
                <div>
                  <h3 className="text-lg font-bold text-slate-800">{service.name}</h3>
                  <div className="flex gap-4 mt-2">
                    {service.hsn_code && <span className="text-xs text-gray-400 font-medium uppercase">HSN: {service.hsn_code}</span>}
                    <span className="text-xs text-gray-400 font-medium uppercase">{service.has_hosting_details === 1 ? '💻 Hosting Specs' : '📄 Standalone'}</span>
                  </div>

                  <div className="mt-6 mb-4">
                    <span className="text-3xl font-black text-violet-800">₹{Number(service.price).toLocaleString('en-IN')}</span>
                    <span className="text-xs text-gray-400 font-bold block mt-1 uppercase">Starting Unit Price</span>
                  </div>
                </div>

                <button
                  onClick={() => handleOpenPurchase(service)}
                  className="w-full mt-2 py-2.5 bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-700 hover:to-indigo-700 text-white text-xs font-bold rounded-xl shadow transition flex items-center justify-center gap-1.5"
                >
                  <FiShoppingCart /> Purchase Sub
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Purchase / Order Confirmation Modal */}
      {isPurchaseModalOpen && selectedService && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 overflow-y-auto">
          <div className="bg-white rounded-3xl w-full max-w-xl shadow-2xl border border-slate-100">
            <div className="p-6 border-b border-slate-100 flex justify-between items-center bg-gradient-to-r from-violet-50/50 to-indigo-50/50">
              <h3 className="text-lg font-black text-slate-800">
                Confirm Service Purchase
              </h3>
              <button
                onClick={() => setIsPurchaseModalOpen(false)}
                className="text-gray-400 hover:text-gray-600 text-xl font-bold"
              >
                ✕
              </button>
            </div>
            <form onSubmit={handleConfirmPurchase} className="p-6 space-y-4">
              <div className="bg-violet-50/50 border border-violet-100 rounded-2xl p-4 flex justify-between items-center">
                <div>
                  <h4 className="text-sm font-bold text-violet-950">{selectedService.name}</h4>
                  <span className="text-xs text-gray-400 mt-1 block">HSN/SAC Code: {selectedService.hsn_code || 'N/A'}</span>
                </div>
                <div className="text-right">
                  <span className="text-lg font-black text-violet-900">₹{Number(selectedService.price).toLocaleString('en-IN')}</span>
                  <span className="text-[10px] text-gray-400 uppercase font-bold block mt-0.5">+ 18% GST</span>
                </div>
              </div>

              {/* Provider Company Selection */}
              <div>
                <label className="block text-xs font-bold text-gray-600 uppercase mb-1">Service Provider Company</label>
                <select
                  value={purchaseForm.company_id}
                  onChange={e => setPurchaseForm(prev => ({ ...prev, company_id: e.target.value }))}
                  className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500 bg-white"
                  required
                >
                  <option value="">Select seller company...</option>
                  {companies.map(comp => (
                    <option key={comp.id} value={comp.id}>{comp.company_name}</option>
                  ))}
                </select>
              </div>

              {/* Billing Cycle Selection */}
              <div>
                <label className="block text-xs font-bold text-gray-600 uppercase mb-1">Billing Cycle</label>
                <select
                  value={purchaseForm.billing_cycle}
                  onChange={e => setPurchaseForm(prev => ({ ...prev, billing_cycle: e.target.value }))}
                  className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500 bg-white"
                  required
                >
                  <option value="Monthly">Monthly</option>
                  <option value="Quarterly">Quarterly</option>
                  <option value="Annually">Annually</option>
                  <option value="One-Time">One-Time</option>
                </select>
              </div>

              {/* Prefilled/Read-only customer billing fields */}
              <div className="bg-slate-50/50 border border-slate-100 rounded-2xl p-4 space-y-2 text-xs">
                <span className="font-extrabold uppercase tracking-wide text-slate-500 block mb-2">Billing Account Profile</span>
                <div className="grid grid-cols-2 gap-2 text-slate-600">
                  <span>Client Name: <strong>{purchaseForm.company_name}</strong></span>
                  <span>Email: <strong>{purchaseForm.email}</strong></span>
                  <span>GSTIN: <strong>{purchaseForm.gst_number || 'N/A'}</strong></span>
                  <span>Billing State: <strong>{purchaseForm.state || 'N/A'}</strong></span>
                </div>
              </div>

              <div className="pt-4 flex justify-end gap-3 border-t border-slate-100">
                <button
                  type="button"
                  onClick={() => setIsPurchaseModalOpen(false)}
                  className="px-4 py-2 border-2 border-slate-200 text-slate-700 bg-white hover:bg-slate-50 rounded-xl text-sm font-semibold transition"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-6 py-2 bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-700 hover:to-indigo-700 text-white rounded-xl text-sm font-semibold shadow transition"
                >
                  Place Order
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default CustomerPortal;
